/**
 * The leave types an employee can apply for.
 *
 * Maternity and Paternity Leave are applyable, but both grant 0 days by default — HR assigns
 * them per employee (see ZERO_GRANT_CODES in leavePolicyService), and the balance check on
 * Apply Leave refuses a request with no entitlement. So appearing in this list makes them
 * requestable, not automatically available.
 *
 * Unpaid Leave has been retired. Historic requests still carry it, which is why
 * ApplyLeavePage keeps a label for the string without it being an option here — the same
 * treatment 'Work From Home' already had.
 */
export enum LeaveType {
  CASUAL = 'Casual Leave',
  SICK = 'Sick Leave',
  EARNED = 'Earned Leave',
  // Comp-Off is applied for like any other leave; the balance it draws down is
  // the one Apply Comp-Off credits on approval, so it must be an applyable type here rather
  // than a balance that can only ever accrue.
  COMP_OFF = 'Comp-Off',
  MATERNITY = 'Maternity Leave',
  PATERNITY = 'Paternity Leave',
}

export enum LeaveStatus {
  SUBMITTED = 'Submitted',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  /**
   * An approved leave the employee has asked to cancel, awaiting their manager.
   *
   * A distinct status rather than a flag because the leave is genuinely in a third
   * state: still booked (it must keep consuming balance until someone decides) but
   * no longer settled. Stored in the same `status` column — the GraphQL field is a
   * plain String, so this needs no schema change.
   */
  CANCELLATION_REQUESTED = 'Cancellation Requested',
  CANCELLED = 'Cancelled',
}

export interface LeaveRequest {
  id: string;
  employeeEmail: string;
  employeeName: string;
  department: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  reason: string;
  status: LeaveStatus;
  createdAt: string;
  /** Half-day session for the leave. Defaults to 'FULL' when omitted (backward compat with existing stored data). */
  session?: 'FULL' | 'HALF_AM' | 'HALF_PM';
  /**
   * Free text attached to the last status change, whoever made it.
   *
   * Doubles as the approver's rejection note and, while the status is
   * CANCELLATION_REQUESTED, as the employee's justification for cancelling — which is
   * what the manager reads on Leave Cancellation before deciding. Declared here rather
   * than cast at the call site so a page that forgets to request the field fails to
   * compile instead of silently rendering a dash.
   */
  decisionReason?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface CreateLeaveRequestInput {
  employeeEmail: string;
  employeeName: string;
  department: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  reason: string;
  /** Half-day session for the leave. Defaults to 'FULL' when omitted (backward compat with existing stored data). */
  session?: 'FULL' | 'HALF_AM' | 'HALF_PM';
}
